import {useEffect, useId, useRef, useSyncExternalStore} from 'react'

import {BREAKPOINT} from '../../webgl/_rawConfig'
import type {BrandedDividerParams} from '../WebGLContext/WebGLContext'
import {useWebGLContext, useWebGLStatus} from '../WebGLContext/WebGLContext'
import {type BrandedDividerViewport, getBrandedDividerFallback} from './brandedDividerFallbacks'

interface WebGLBrandedDividerProps {
  className?: string
  params: BrandedDividerParams
}

type DividerPresentation = Readonly<{
  aspectRatio: string
  viewport: BrandedDividerViewport
}>

const MOBILE_QUERY = `(max-width: ${BREAKPOINT.MOBILE}px)`
const TABLET_QUERY = `(max-width: ${BREAKPOINT.TABLET}px)`
const PRESENTATION_BY_VIEWPORT: Record<BrandedDividerViewport, DividerPresentation> = {
  mobile: {aspectRatio: '390 / 115', viewport: 'mobile'},
  tablet: {aspectRatio: '800 / 131', viewport: 'tablet'},
  desktop: {aspectRatio: '1600 / 157', viewport: 'desktop'},
}

let mobileMediaQuery: MediaQueryList | null = null
let tabletMediaQuery: MediaQueryList | null = null

function getMediaQueries() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  mobileMediaQuery ||= window.matchMedia(MOBILE_QUERY)
  tabletMediaQuery ||= window.matchMedia(TABLET_QUERY)
  return {mobile: mobileMediaQuery, tablet: tabletMediaQuery}
}

function subscribeToViewport(onChange: () => void) {
  const mediaQueries = getMediaQueries()
  if (!mediaQueries) return () => {}

  mediaQueries.mobile.addEventListener('change', onChange)
  mediaQueries.tablet.addEventListener('change', onChange)
  return () => {
    mediaQueries.mobile.removeEventListener('change', onChange)
    mediaQueries.tablet.removeEventListener('change', onChange)
  }
}

function getViewportSnapshot(): BrandedDividerViewport {
  const mediaQueries = getMediaQueries()
  if (mediaQueries?.mobile.matches) return 'mobile'
  if (mediaQueries?.tablet.matches) return 'tablet'
  return 'desktop'
}

function useDividerPresentation() {
  const getServerSnapshot = (): BrandedDividerViewport => 'desktop'
  const viewport = useSyncExternalStore(subscribeToViewport, getViewportSnapshot, getServerSnapshot)
  return PRESENTATION_BY_VIEWPORT[viewport]
}

export function WebGLBrandedDivider({className, params}: WebGLBrandedDividerProps) {
  const {character, layout} = params
  const {registerBrandedDivider, unregisterBrandedDivider} = useWebGLContext()
  const webGLStatus = useWebGLStatus()
  const ref = useRef<HTMLDivElement>(null)
  const id = useId()
  const presentation = useDividerPresentation()

  useEffect(() => {
    if (ref.current) {
      registerBrandedDivider({id, element: ref.current, params})
    }

    return () => {
      unregisterBrandedDivider(id)
    }
    // Mount-only by design: `id` and the context callbacks are stable, but
    // `params` is an inline object literal at every call site, so a full dep
    // array would unregister/re-register the WebGL layer on every render.
    // Changing `params` after mount is therefore not reflected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const element = ref.current
    if (!element) return

    element.style.backgroundImage = ''
    if (webGLStatus !== 'unavailable') return

    let isCancelled = false
    void getBrandedDividerFallback({character, layout}, presentation.viewport)
      .then(fallbackImage => {
        if (!isCancelled && fallbackImage) {
          element.style.backgroundImage = `url("${fallbackImage}")`
        }
      })
      .catch(() => {})

    return () => {
      isCancelled = true
    }
  }, [character, layout, presentation.viewport, webGLStatus])

  return (
    <div
      ref={ref}
      className={className}
      style={{
        width: '100%',
        maxWidth: '1600px',
        aspectRatio: presentation.aspectRatio,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
      }}
    />
  )
}
